import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { FcFlowModel } from "../../entities/fcFlow";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";

export const fcFlowApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getFcFlowCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isCumulative: boolean) => `/fcFlow/${isCumulative}/commonInfo`,
      providesTags: [{ type: TagTypes.FcFlow, id: "LIST" }],
    }),
    getFcFlowFilteredPaged: builder.query<FilteredPagedQueryResult<FcFlowModel>, FilteredPagedModel>({
      query: ({ isCumulative, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/fcFlow/${isCumulative}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result) =>
        result
          ? [
              { type: TagTypes.FcFlow, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.FcFlow,
                id,
              })),
            ]
          : [{ type: TagTypes.FcFlow, id: "PARTIAL-LIST" }],
    }),
    getFcFlow: builder.query<FcFlowModel, number>({
      query: (fcFlowId) => `/fcFlow/${fcFlowId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.FcFlow, id: arg }],
    }),
    scrapFcFlow: builder.query<FcFlowModel, { isCumulative: boolean; tracingNumber: string }>({
      query: ({ isCumulative, tracingNumber }) => ({
        url: `/fcFlow/scrap/${isCumulative}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.FcFlow, id: "Scrap" }],
    }),
    addFcFlow: builder.mutation({
      query: (initialFcFlow) => ({
        url: "/fcFlow",
        method: "POST",
        body: initialFcFlow,
      }),
      invalidatesTags: [
        { type: TagTypes.FcFlow, id: "LIST" },
        { type: TagTypes.FcFlow, id: "PARTIAL-LIST" },
      ],
    }),
    editFcFlow: builder.mutation({
      query: (fcFlow) => ({
        url: `/fcFlow/${fcFlow.id}`,
        method: "PUT",
        body: fcFlow,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.FcFlow, id: arg.id }],
    }),
    deleteFcFlow: builder.mutation({
      query: (fcFlowId) => ({
        url: `/fcFlow/${fcFlowId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.FcFlow, id: arg },
              { type: TagTypes.FcFlow, id: "LIST" },
            ],
    }),
    deleteMultiFcFlows: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/fcFlow/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.FcFlow,
                id,
              })),
              { type: TagTypes.FcFlow, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetFcFlowCommonInfoQuery,
  useGetFcFlowFilteredPagedQuery,
  useGetFcFlowQuery,
  useScrapFcFlowQuery,
  useLazyScrapFcFlowQuery,
  useAddFcFlowMutation,
  useEditFcFlowMutation,
  useDeleteFcFlowMutation,
  useDeleteMultiFcFlowsMutation,
} = fcFlowApi;
